<template>
  <div class="mx-auto max-w-7xl px-4 py-8 font-inter">
    <div class="mb-6 flex items-center gap-2 text-sm font-medium text-gray-600">
      <RouterLink
        :to="backLinkTarget"
        class="inline-flex items-center gap-2 hover:text-newPrimary"
      >
        <ArrowLeft class="h-4 w-4" />
        Back
      </RouterLink>
    </div>

    <div v-if="isLoading" class="space-y-6 animate-pulse">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div class="lg:col-span-2 space-y-6">
          <div class="rounded-lg border border-border bg-card p-6">
            <div class="mb-4 h-6 w-40 rounded bg-muted"></div>
            <div class="flex items-center gap-6">
              <div class="h-20 w-20 rounded-md bg-muted"></div>
              <div class="flex-1 space-y-2">
                <div class="h-6 w-1/2 rounded bg-muted"></div>
                <div class="h-4 w-1/3 rounded bg-muted"></div>
              </div>
            </div>
          </div>
          <div class="rounded-lg border border-border bg-card p-6 space-y-3">
            <div class="h-5 w-32 rounded bg-muted"></div>
            <div class="h-3 w-full rounded bg-muted"></div>
            <div class="h-3 w-5/6 rounded bg-muted"></div>
            <div class="h-3 w-4/6 rounded bg-muted"></div>
          </div>
          <div class="rounded-lg border border-border bg-card p-6 space-y-4">
            <div class="h-5 w-48 rounded bg-muted"></div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="h-20 rounded bg-muted"></div>
              <div class="h-20 rounded bg-muted"></div>
            </div>
          </div>
        </div>
        <div class="lg:col-span-1 space-y-6">
          <div class="rounded-lg border border-border bg-card p-6 space-y-3">
            <div class="h-5 w-36 rounded bg-muted"></div>
            <div class="h-3 w-full rounded bg-muted"></div>
            <div class="h-3 w-5/6 rounded bg-muted"></div>
            <div class="h-3 w-4/6 rounded bg-muted"></div>
          </div>
          <div class="rounded-lg border border-border bg-card p-6 space-y-3">
            <div class="h-5 w-40 rounded bg-muted"></div>
            <div class="h-3 w-full rounded bg-muted"></div>
            <div class="h-3 w-5/6 rounded bg-muted"></div>
            <div class="h-3 w-4/6 rounded bg-muted"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="company" class="grid grid-cols-1 gap-8 lg:grid-cols-3">
      <div class="lg:col-span-2 space-y-8">
        <div class="rounded-lg border border-border bg-card p-6 shadow-sm">
          <div class="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-start sm:gap-8 sm:text-left">
            <div class="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl bg-gray-50 p-2 shadow-inner">
              <img
                v-if="company.logo"
                :src="company.logo"
                alt="Company logo"
                class="max-h-20 max-w-full object-contain"
              />
              <Building2 v-else class="h-10 w-10 text-gray-300" />
            </div>
            <div class="min-w-0 max-w-full">
              <div class="flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                <h1 class="text-3xl font-bold leading-tight text-gray-900">
                  {{ company.name }}
                </h1>
                <button
                  v-if="company.id"
                  type="button"
                  class="inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-newPrimary/20 disabled:cursor-not-allowed disabled:opacity-60"
                  :class="company.is_bookmarked
                    ? 'border-newPrimary bg-newPrimary text-white hover:bg-newPrimary/90'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-newPrimary hover:text-newPrimary'"
                  :disabled="isBookmarkSaving"
                  @click="toggleCompanyBookmark"
                >
                  <Bookmark class="h-4 w-4" :class="{ 'fill-current': company.is_bookmarked }" />
                  {{ company.is_bookmarked ? 'Bookmarked' : 'Bookmark' }}
                </button>
              </div>
              <p
                v-if="showMarketCap || company.is_private_company || company.ticker"
                class="mt-2 text-sm font-medium text-gray-700"
              >
                <span v-if="showMarketCap">{{ formattedMarketCapLabel }} Market Cap</span>
                <span v-else-if="company.is_private_company">Private company</span>
                <span v-if="company.ticker">
                  <span v-if="showMarketCap || company.is_private_company"> · </span>ASX:{{ company.ticker }}
                </span>
              </p>
              <p v-if="company.head_office" class="mt-1 text-sm font-medium text-gray-700">
                {{ company.head_office }}
              </p>
              <div
                v-if="(websiteHref && displayWebsite) || contactEmailHref"
                class="mt-2 flex max-w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-gray-600 sm:justify-start"
              >
                <a
                  v-if="websiteHref && displayWebsite"
                  :href="websiteHref"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex max-w-full min-w-0 items-center gap-1.5 hover:text-newPrimary"
                >
                  <Link class="h-4 w-4 shrink-0" />
                  <span class="truncate">{{ displayWebsite }}</span>
                </a>
                <a
                  v-if="contactEmailHref"
                  :href="contactEmailHref"
                  class="inline-flex max-w-full min-w-0 items-center gap-1.5 hover:text-newPrimary"
                >
                  <Mail class="h-4 w-4 shrink-0" />
                  <span class="truncate">{{ company.contact_email }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <section
          v-if="company.summary"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <h3 class="mb-2 text-lg font-semibold text-gray-900">
            About Us
          </h3>
          <p class="text-sm leading-relaxed text-gray-600">
            {{ company.summary }}
          </p>
        </section>

        <section
          v-if="company.key_projects.length"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <h3 class="mb-4 text-lg font-semibold text-gray-900">Key Projects</h3>
          <div v-if="projectCommodities.length" class="mb-5 border-b border-border/60 pb-5">
            <p class="mb-2 text-sm font-semibold text-gray-900">Commodities Across Projects</p>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="item in projectCommodities"
                :key="item"
                class="rounded-full bg-newPrimary/10 px-2.5 py-1 text-xs font-semibold text-newPrimary"
              >
                {{ item }}
              </span>
            </div>
          </div>
          <div class="space-y-4">
            <div
              v-for="(project, index) in company.key_projects"
              :key="index"
              class="rounded-lg border border-border/60 bg-background/80 p-4"
            >
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-semibold text-gray-900">{{ project.name || 'Untitled project' }}</span>
                <span v-if="project.stage" class="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                  {{ project.stage }}
                </span>
              </div>

              <p v-if="project.location_label" class="mt-2 text-sm text-gray-600">
                {{ project.location_label }}
              </p>

              <div v-if="project.description" class="mt-3">
                <p
                  class="text-sm leading-relaxed text-gray-600"
                  :class="{ 'line-clamp-2': !isProjectDescriptionExpanded(index) }"
                >
                  {{ project.description }}
                </p>
                <button
                  v-if="shouldShowTextToggle(project.description)"
                  type="button"
                  class="mt-1 text-xs font-medium text-newPrimary hover:underline"
                  @click="toggleProjectDescription(index)"
                >
                  {{ isProjectDescriptionExpanded(index) ? 'Show less' : 'Read more' }}
                </button>
              </div>

              <GoogleMap
                v-if="project.location_label || project.location_lat || project.location_lng"
                class="mt-3"
                :address="project.location_label"
                :lat="project.location_lat ?? null"
                :lng="project.location_lng ?? null"
                map-height-class="h-40"
                :zoom="7"
              />

              <div v-if="project.commodities.length" class="mt-3 flex flex-wrap gap-2">
                <span
                  v-for="item in project.commodities"
                  :key="item"
                  class="rounded-full bg-newPrimary/10 px-2.5 py-1 text-xs font-semibold text-newPrimary"
                >
                  {{ item }}
                </span>
              </div>

              <div v-if="project.activity_feed.length" class="mt-4 border-t border-border/60 pt-3">
                <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Activity Feed</p>
                <div class="space-y-3">
                  <div
                    v-for="(activity, activityIndex) in project.activity_feed"
                    :key="`${activity.date}-${activityIndex}`"
                    class="text-sm"
                  >
                    <p v-if="activity.date" class="text-xs font-semibold text-gray-500">
                      {{ formatDateAsDayMonthYear(activity.date) }}
                    </p>
                    <div class="mt-1">
                      <p
                        class="leading-relaxed text-gray-700"
                        :class="{ 'line-clamp-2': !isActivityExpanded(index, activityIndex) }"
                      >
                        {{ activity.text }}
                      </p>
                      <button
                        v-if="shouldShowTextToggle(activity.text)"
                        type="button"
                        class="mt-1 text-xs font-medium text-newPrimary hover:underline"
                        @click="toggleActivity(index, activityIndex)"
                      >
                        {{ isActivityExpanded(index, activityIndex) ? 'Show less' : 'Read more' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="companyJobLinks.length"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <div class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Current Job Opportunities</h3>
              <p class="text-sm text-gray-600">
                Explore active hiring pages and career destinations for {{ company.name }}.
              </p>
            </div>
            <span class="inline-flex w-fit items-center rounded-full bg-newPrimary/10 px-3 py-1 text-xs font-semibold text-newPrimary">
              {{ companyJobLinks.length }} live {{ companyJobLinks.length === 1 ? 'link' : 'links' }}
            </span>
          </div>

          <div
            class="mt-5 grid grid-cols-1 gap-4"
            :class="{
              'md:grid-cols-2': companyJobLinks.length === 2,
              'md:grid-cols-3': companyJobLinks.length >= 3,
            }"
          >
            <a
              v-for="jobLink in companyJobLinks"
              :key="`${jobLink.type}-${jobLink.url}`"
              :href="jobLink.url"
              target="_blank"
              rel="noopener noreferrer"
              class="group rounded-xl border border-border/70 bg-gradient-to-br p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-newPrimary/40 hover:shadow-md"
              :class="jobLink.cardClass"
            >
              <div class="flex items-start justify-between gap-4">
                <div class="flex h-11 w-11 items-center justify-center rounded-xl border border-white/70 bg-white/80 text-gray-700 shadow-sm">
                  <component :is="jobLink.icon" class="h-5 w-5" />
                </div>
                <span class="rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-600">
                  {{ jobLink.typeLabel }}
                </span>
              </div>

              <div class="mt-5">
                <h4 class="text-base font-semibold text-gray-900">
                  {{ jobLink.title }}
                </h4>
                <p class="mt-2 text-sm leading-6 text-gray-600">
                  {{ jobLink.description }}
                </p>
              </div>

              <div class="mt-5 flex items-center justify-between text-sm font-medium text-gray-700">
                <span>{{ jobLink.ctaLabel }}</span>
                <ArrowUpRight class="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </a>
          </div>
        </section>

        <section
          v-if="company.manual_jobs.length"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <div class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Live Jobs</h3>
              <p class="text-sm text-gray-600">
                Current opportunities from {{ company.name }}'s hiring channels — view roles and go straight to the source to apply.
              </p>
            </div>
            <span class="inline-flex w-fit items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
              {{ company.manual_jobs.length }} job{{ company.manual_jobs.length === 1 ? '' : 's' }}
            </span>
          </div>

          <div class="mt-5 space-y-4">
            <component
              v-for="job in company.manual_jobs"
              :is="job.is_imported ? RouterLink : 'a'"
              :key="job.id ?? `${job.title}-${job.job_url}`"
              :to="job.is_imported && job.job_slug ? `/contractor/job/${job.job_slug}` : undefined"
              :href="!job.is_imported ? (job.job_url || undefined) : undefined"
              :target="!job.is_imported && job.job_url ? '_blank' : undefined"
              :rel="!job.is_imported && job.job_url ? 'noopener noreferrer' : undefined"
              class="group block rounded-xl border border-border/70 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300/70 hover:shadow-md"
              :class="{ 'pointer-events-none': job.is_imported ? !job.job_slug : !job.job_url }"
            >
              <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      v-if="job.employment_type"
                      class="rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-600"
                    >
                      {{ job.employment_type }}
                    </span>
                    <span
                      v-if="job.source_label"
                      class="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700"
                    >
                      {{ job.source_label }}
                    </span>
                  </div>

                  <h4 class="mt-3 text-base font-semibold text-gray-900">
                    {{ job.title }}
                  </h4>

                  <p
                    v-if="job.summary"
                    class="mt-2 whitespace-pre-line text-sm leading-6 text-gray-600"
                  >
                    {{ job.summary }}
                  </p>

                  <div class="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600">
                    <span v-if="job.location" class="flex items-center gap-1.5">
                      <MapPin class="h-4 w-4 text-emerald-600" />
                      {{ job.location }}
                    </span>
                    <span v-if="job.roster" class="flex items-center gap-1.5">
                      <Briefcase class="h-4 w-4 text-emerald-600" />
                      {{ job.roster }} roster
                    </span>
                    <span v-if="formatCompensation(job)" class="font-medium text-gray-700">
                      {{ formatCompensation(job) }}
                    </span>
                  </div>

                  <div
                    v-if="job.date_posted || job.closing_date"
                    class="mt-4 flex flex-wrap gap-2"
                  >
                    <span
                      v-if="job.date_posted"
                      class="inline-flex items-center rounded-full border border-emerald-200 bg-white/90 px-3 py-1 text-xs font-medium text-emerald-700"
                    >
                      Posted {{ formatDateAsDayMonthYear(job.date_posted) }}
                    </span>
                    <span
                      v-if="job.closing_date"
                      class="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700"
                    >
                      Closes {{ formatDateAsDayMonthYear(job.closing_date) }}
                    </span>
                  </div>
                </div>
              </div>

              <div
                v-if="job.job_url || job.job_slug"
                class="mt-5 flex items-center justify-between text-sm font-medium text-gray-700"
              >
                <span>{{ job.is_imported ? 'View full job' : 'Open job posting' }}</span>
                <ArrowUpRight class="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </component>
          </div>
        </section>

        <section class="space-y-4">
          <h3 class="text-lg font-semibold text-gray-900">
            Open Talent Pools at {{ company.name }}
          </h3>

          <div
        v-if="company.talentPools.length"
            class="grid grid-cols-1 gap-6 md:grid-cols-2"
          >
            <component
              v-for="pool in company.talentPools"
              :is="pool.id ? RouterLink : 'div'"
              :key="pool.id ?? pool.title"
              :to="pool.id ? { name: 'TalentCommunityDetail', params: { id: pool.id } } : undefined"
              class="flex flex-col gap-2 rounded-lg border border-border bg-card p-5 shadow-sm transition hover:border-newPrimary/60"
            >
              <h4 class="text-lg font-semibold text-gray-900">{{ pool.title }}</h4>
              <div class="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span v-if="pool.location" class="flex items-center gap-1">
                  <MapPin class="h-4 w-4 text-newPrimary" />
                  {{ pool.location }}
                </span>
                <span v-if="pool.type" class="rounded-full bg-newPrimary/10 px-2 py-0.5 text-xs font-semibold text-newPrimary">
                  {{ pool.type }}
                </span>
              </div>
              <p class="text-xs text-gray-500">
                {{ formatHiringTimeline(pool.hiring_timeline, pool.timeline_date, pool.timeline) }}
              </p>
            </component>
          </div>

          <div
            v-else
            class="rounded-lg border border-border bg-card px-6 py-12 text-center shadow-sm"
          >
            <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <Briefcase class="h-8 w-8 text-gray-500" />
            </div>
            <h4 class="text-lg font-medium text-gray-900">No Active Talent Pools</h4>
            <p class="mt-1 text-sm text-gray-500">
              This company doesn't have any open talent pools right now. Check back later!
            </p>
          </div>
        </section>
      </div>

      <div class="lg:col-span-1 space-y-6">
        <section
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <h3 class="border-b pb-2 text-lg font-semibold text-gray-900">
            Company Overview
          </h3>
          <div class="mt-4 space-y-4">
            <div v-if="company.industry">
              <p class="text-xs text-gray-500">Industry</p>
              <p class="font-medium text-gray-800">{{ company.industry }}</p>
            </div>
            <div v-for="group in company.industry_attributes" :key="group.key">
              <p class="mb-2 text-xs text-gray-500">{{ group.label }}</p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="value in group.values"
                  :key="value"
                  class="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700"
                >
                  {{ value }}
                </span>
              </div>
            </div>
            <div v-if="company.employee_size">
              <p class="text-xs text-gray-500">Employee Size</p>
              <p class="font-medium text-gray-800">{{ company.employee_size }}</p>
            </div>
            <div v-if="company.headcount_growth_12_months">
              <p class="text-xs text-gray-500">Headcount Growth (Last 12 Months)</p>
              <p class="font-medium text-gray-800">{{ formatPercentValue(company.headcount_growth_12_months) }}</p>
            </div>
            <div v-if="company.average_employee_tenure">
              <p class="text-xs text-gray-500">Average Employee Tenure</p>
              <p class="font-medium text-gray-800">{{ formatYearsValue(company.average_employee_tenure) }}</p>
            </div>
            <div v-if="cashInBankLabel && cashInBankDateLabel">
              <p class="text-xs text-gray-500">Cash in Bank</p>
              <p class="font-medium text-gray-800">{{ cashInBankLabel }}</p>
              <p class="text-xs text-gray-500">As of {{ cashInBankDateLabel }}</p>
            </div>
            <div v-if="cashRunwayLabel">
              <p class="text-xs text-gray-500">Cash Runway</p>
              <p class="font-medium text-gray-800">{{ cashRunwayLabel }}</p>
            </div>
            <div v-if="company.key_people.length">
              <p class="mb-1 text-xs text-gray-500">Key People</p>
              <ul class="space-y-2">
                <li
                  v-for="(person, idx) in company.key_people"
                  :key="idx"
                  class="flex items-center gap-3"
                >
                  <div class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600">
                    {{ personInitial(person) }}
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-800">{{ personName(person) }}</p>
                    <p class="text-xs text-gray-500">{{ personRole(person) }}</p>
                    <p
                      v-if="personRoleTenure(person)"
                      class="text-xs text-gray-500"
                    >
                      Current Role Tenure: {{ personRoleTenure(person) }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section
          v-if="company.recent_capital_raisings.length"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <h3 class="border-b pb-2 text-lg font-semibold text-gray-900">Recent Capital Raisings</h3>
          <div class="mt-4 space-y-4">
            <div
              v-for="(raising, index) in company.recent_capital_raisings"
              :key="index"
              class="rounded-lg border border-border/60 bg-background/80 p-4"
            >
              <div class="flex flex-wrap items-center gap-2">
                <span
                  v-if="raising.label"
                  class="text-sm font-semibold text-newPrimary"
                >
                  {{ raising.label }}
                </span>
                <span
                  v-if="raising.date"
                  class="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700"
                >
                  {{ formatDateAsDayMonthYear(raising.date) }}
                </span>
              </div>

              <div class="mt-3 space-y-3">
                <div class="min-w-0">
                  <p
                    v-if="raising.amount"
                    class="text-base font-semibold text-gray-900"
                  >
                    {{ formatCapitalRaisingAmount(raising.amount) }}
                  </p>
                  <p
                    v-if="raising.summary"
                    class="mt-1 text-sm leading-relaxed text-gray-600"
                  >
                    {{ raising.summary }}
                  </p>
                </div>

                <a
                  v-if="raising.url"
                  :href="normalizeExternalUrl(raising.url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-newPrimary hover:underline"
                >
                  Source
                  <ArrowUpRight class="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="company.recent_key_hires.length"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <h3 class="border-b pb-2 text-lg font-semibold text-gray-900">Executive Movements</h3>
          <div class="mt-4 space-y-4">
            <div
              v-for="(hire, idx) in company.recent_key_hires"
              :key="idx"
              class="rounded-lg border border-border/60 bg-background/80 p-4"
            >
              <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="text-sm font-semibold text-gray-900">{{ hire.name }}</p>
                    <span
                      v-if="hire.date"
                      class="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700"
                    >
                      {{ formatDateAsDayMonthYear(hire.date) }}
                    </span>
                  </div>
                  <p
                    v-if="hire.role"
                    class="mt-1 text-sm font-medium text-gray-700"
                  >
                    {{ hire.role }}
                  </p>
                  <p
                    v-if="hire.background"
                    class="mt-2 text-xs uppercase tracking-[0.14em] text-gray-500"
                  >
                    {{ hire.background }}
                  </p>
                  <p
                    v-if="hire.summary"
                    class="mt-2 text-sm leading-relaxed text-gray-600"
                  >
                    {{ hire.summary }}
                  </p>
                </div>

                <a
                  v-if="hire.url"
                  :href="normalizeExternalUrl(hire.url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-newPrimary hover:underline"
                >
                  Source
                  <ArrowUpRight class="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="company.benefits.length"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div class="min-w-0 flex-1">
              <h3 class="text-lg font-semibold text-gray-900">Benefits</h3>
              <p class="text-sm text-gray-600">
                Additional benefits and perks shared by {{ company.name }}.
              </p>
            </div>
            <span class="inline-flex w-fit shrink-0 self-start rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
              {{ company.benefits.length }} benefit{{ company.benefits.length === 1 ? '' : 's' }}
            </span>
          </div>

          <div class="mt-5 space-y-4">
            <div
              v-for="(benefit, idx) in company.benefits"
              :key="`${benefit.heading}-${benefit.source}-${idx}`"
              class="rounded-lg border border-border/60 bg-background/80 p-4"
            >
              <div class="space-y-4">
                <div class="min-w-0">
                  <p
                    v-if="benefit.heading"
                    class="text-base font-semibold text-gray-900"
                  >
                    {{ benefit.heading }}
                  </p>
                  <p
                    v-if="benefit.description"
                    class="mt-2 text-sm leading-relaxed text-gray-600"
                  >
                    {{ benefit.description }}
                  </p>
                </div>

                <div
                  v-if="benefit.source || benefit.added_at"
                  class="flex flex-wrap items-center gap-2 border-t border-border/60 pt-3"
                >
                  <span
                    v-if="benefit.source"
                    class="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700"
                  >
                    Source: {{ benefit.source }}
                  </span>
                  <span
                    v-if="benefit.added_at"
                    class="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"
                  >
                    Added {{ formatDateAsDayMonthYear(benefit.added_at) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="socialLinkDisplayItems.length"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <h3 class="border-b pb-2 text-lg font-semibold text-gray-900">Social Media</h3>
          <div class="mt-4 space-y-3">
            <a
              v-for="social in socialLinkDisplayItems"
              :key="`${social.type}-${social.url}`"
              :href="normalizeExternalUrl(social.url)"
              target="_blank"
              rel="noopener noreferrer"
              class="group flex items-center justify-between rounded-lg border border-border/60 bg-background/80 px-4 py-3 transition hover:border-newPrimary/40 hover:bg-white"
            >
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-900">{{ social.label }}</p>
                <p class="truncate text-xs text-gray-500">{{ social.displayUrl }}</p>
              </div>
              <div class="ml-4 flex shrink-0 items-center gap-2 text-xs font-medium text-newPrimary">
                <span>Open</span>
                <ArrowUpRight class="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </a>
          </div>
        </section>

        <section
          v-if="operationsVisible"
          class="rounded-lg border border-border bg-card p-6 shadow-sm"
        >
          <h3 class="border-b pb-2 text-lg font-semibold text-gray-900">On-Site Operations</h3>
          <div class="mt-4 space-y-4">
            <div v-if="company.field_roster">
              <p class="text-xs text-gray-500">Field Roster</p>
              <p class="font-medium text-gray-800">{{ company.field_roster }}</p>
            </div>
            <div v-if="company.typical_hours">
              <p class="text-xs text-gray-500">Typical Hours</p>
              <p class="font-medium text-gray-800">{{ company.typical_hours }}</p>
            </div>
            <div v-if="company.facilities">
              <p class="text-xs text-gray-500">Facilities</p>
              <p class="font-medium text-gray-800">{{ company.facilities }}</p>
            </div>
            <div v-if="company.software_used.length">
              <p class="mb-2 text-xs text-gray-500">Software Used</p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="software in company.software_used"
                  :key="software"
                  class="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700"
                >
                  {{ software }}
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <div
      v-else
      class="rounded-lg border border-border bg-red-50 p-6 text-red-700"
    >
      {{ error || 'Company not found.' }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import {
  ArrowLeft,
  ArrowUpRight,
  Link,
  Bookmark,
  Building2,
  Briefcase,
  Globe,
  Mail,
  MapPin,
} from 'lucide-vue-next'
import {
  bookmarkContractorCompany,
  getContractorCompanyProfile,
  removeContractorCompanyBookmark,
} from '@/api/contractor/company'
import { formatDateAsDayMonthYear, formatHiringTimeline } from '@/utils/formatters'
import GoogleMap from '@/components/marketplace/generic/GoogleMap.vue'
import { formatMarketCap } from '@/composables/useCompanyMarketData'

type KeyPerson = {
  first_name?: string
  last_name?: string
  role?: string
  role_start_date?: string
  roleStartDate?: string
  name?: string
  position?: string
}

type KeyProject = {
  name: string
  stage: string
  description: string
  location_label: string
  location_lat: number | null
  location_lng: number | null
  commodities: string[]
  activity_feed: Array<{ date: string; text: string }>
}

type TalentPoolSummary = {
  id: number | null
  title: string
  location: string
  type: string
  work_type: number | null
  hiring_timeline?: string | null
  timeline_date: string | null
  timeline?: string | null
}

type JobLinkType = 'linkedin' | 'seek' | 'other'

type CompanyJobLink = {
  type: JobLinkType
  title: string
  description: string
  url: string
}

type RecentCapitalRaising = {
  date: string
  amount: string
  label: string
  summary: string
  url: string
}

type RecentKeyHire = {
  name: string
  role: string
  date: string
  background: string
  summary: string
  url: string
}

type CompanyBenefit = {
  heading: string
  description: string
  source: string
  added_at: string
}

type SocialLinkType = 'linkedin' | 'facebook' | 'instagram' | 'x' | 'youtube'

type CompanySocialLink = {
  type: SocialLinkType
  url: string
}

type CompanyProfile = {
  id: number | null
  name: string
  logo: string
  website: string
  contact_email: string
  summary: string
  industry: string
  industry_attributes: Array<{ key: string; label: string; values: string[] }>
  employee_size: string
  headcount_growth_12_months: string
  average_employee_tenure: string
  head_office: string
  market_cap: number | string | null
  cash_in_bank: number | null
  cash_in_bank_updated_at: string | null
  cash_runway: string
  ticker: string
  is_private_company: boolean
  is_bookmarked: boolean
  key_people: KeyPerson[]
  key_projects: KeyProject[]
  commodities: string[]
  field_roster: string
  typical_hours: string
  facilities: string
  software_used: string[]
  talentPools: TalentPoolSummary[]
  job_links: CompanyJobLink[]
  recent_capital_raisings: RecentCapitalRaising[]
  recent_key_hires: RecentKeyHire[]
  benefits: CompanyBenefit[]
  social_links: CompanySocialLink[]
  manual_jobs: CompanyManualJob[]
}

type CompanyManualJob = {
  id: number | null
  title: string
  summary: string
  location: string
  employment_type: string
  roster: string
  compensation_mode: string
  compensation_amount: string
  compensation_rate_type: string
  date_posted: string
  closing_date: string
  job_url: string
  job_slug: string
  is_imported: boolean
  source_origin: string
  source_label: string
}

type JobLinkDisplayItem = CompanyJobLink & {
  typeLabel: string
  ctaLabel: string
  cardClass: string
  icon: typeof Globe
}

const route = useRoute()

const company = ref<CompanyProfile | null>(null)
const isLoading = ref(true)
const isBookmarkSaving = ref(false)
const error = ref<string | null>(null)
const expandedProjectDescriptions = ref<Set<number>>(new Set())
const expandedActivityItems = ref<Set<string>>(new Set())
const projectCommodities = computed(() => {
  const projects = company.value?.key_projects ?? []
  return Array.from(
    new Set(
      projects.flatMap((project) =>
        Array.isArray(project.commodities)
          ? project.commodities.map((item) => item.trim()).filter(Boolean)
          : []
      )
    )
  ).sort((a, b) => a.localeCompare(b))
})
const backLinkTarget = computed(() => {
  if (route.query.from === 'jobs') {
    return { name: 'ContractorJobs' }
  }

  return {
    name: 'ContractorCompanySearch',
    query: route.query,
  }
})

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'auto' })
})

const toSafeString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')
const shouldShowTextToggle = (value: string): boolean => toSafeString(value).length > 140
const activityKey = (projectIndex: number, activityIndex: number): string => `${projectIndex}:${activityIndex}`

const isProjectDescriptionExpanded = (projectIndex: number): boolean =>
  expandedProjectDescriptions.value.has(projectIndex)

const toggleProjectDescription = (projectIndex: number) => {
  const next = new Set(expandedProjectDescriptions.value)
  if (next.has(projectIndex)) {
    next.delete(projectIndex)
  } else {
    next.add(projectIndex)
  }
  expandedProjectDescriptions.value = next
}

const isActivityExpanded = (projectIndex: number, activityIndex: number): boolean =>
  expandedActivityItems.value.has(activityKey(projectIndex, activityIndex))

const toggleActivity = (projectIndex: number, activityIndex: number) => {
  const key = activityKey(projectIndex, activityIndex)
  const next = new Set(expandedActivityItems.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  expandedActivityItems.value = next
}

const parseMoney = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const filtered = value.replace(/[^0-9.]/g, '')
    const parsed = Number(filtered)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}
const parseCoordinate = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const formatDateLabel = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString()
}

const normalizeJobLinks = (value: unknown): CompanyJobLink[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null

      const type = toSafeString((entry as any).type).toLowerCase() as JobLinkType
      if (!['linkedin', 'seek', 'other'].includes(type)) return null

      const url = toSafeString((entry as any).url ?? '')
      if (!url) return null

      return {
        type,
        title: toSafeString((entry as any).title ?? ''),
        description: toSafeString((entry as any).description ?? ''),
        url,
      }
    })
    .filter((entry): entry is CompanyJobLink => Boolean(entry))
}

const normalizeSocialLinks = (value: unknown): CompanySocialLink[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null

      const type = toSafeString((entry as any).type).toLowerCase() as SocialLinkType
      if (!['linkedin', 'facebook', 'instagram', 'x', 'youtube'].includes(type)) return null

      const url = toSafeString((entry as any).url ?? '')
      if (!url) return null

      return { type, url }
    })
    .filter((entry): entry is CompanySocialLink => Boolean(entry))
}

const normalizeRecentKeyHires = (value: unknown): RecentKeyHire[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null

      const item = {
        name: toSafeString((entry as any).name ?? ''),
        role: toSafeString((entry as any).role ?? (entry as any).title ?? ''),
        date: toSafeString((entry as any).date ?? ''),
        background: toSafeString((entry as any).background ?? ''),
        summary: toSafeString((entry as any).summary ?? ''),
        url: toSafeString((entry as any).url ?? ''),
      }

      return item.name || item.role || item.date || item.background || item.summary || item.url
        ? item
        : null
    })
    .filter((entry): entry is RecentKeyHire => Boolean(entry))
}

const normalizeRecentCapitalRaisings = (value: unknown): RecentCapitalRaising[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null

      const item = {
        date: toSafeString((entry as any).date ?? ''),
        amount: toSafeString((entry as any).amount ?? ''),
        label: toSafeString((entry as any).label ?? (entry as any).type ?? ''),
        summary: toSafeString((entry as any).summary ?? (entry as any).notes ?? ''),
        url: toSafeString((entry as any).url ?? ''),
      }

      return item.date || item.amount || item.label || item.summary || item.url ? item : null
    })
    .filter((entry): entry is RecentCapitalRaising => Boolean(entry))
}

const normalizeBenefits = (value: unknown): CompanyBenefit[] => {
  if (!Array.isArray(value)) return []

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null

      const item = {
        heading: toSafeString((entry as any).heading ?? (entry as any).title ?? ''),
        description: toSafeString((entry as any).description ?? (entry as any).summary ?? ''),
        source: toSafeString((entry as any).source ?? ''),
        added_at: toSafeString((entry as any).added_at ?? (entry as any).date ?? ''),
      }

      return item.heading || item.description || item.source ? item : null
    })
    .filter((entry): entry is CompanyBenefit => Boolean(entry))
}

const normalizeExternalUrl = (value: string): string =>
  /^https?:\/\//i.test(value) ? value : `https://${value}`

const normalizeCompanyPayload = (payload: any): CompanyProfile => {
  const normalizePeople = Array.isArray(payload.key_people)
    ? payload.key_people.map((person: any) => ({
      ...person,
      first_name: toSafeString(person?.first_name ?? person?.firstName ?? ''),
      last_name: toSafeString(person?.last_name ?? person?.lastName ?? ''),
      role: toSafeString(person?.role ?? person?.position ?? ''),
      role_start_date: toSafeString(person?.role_start_date ?? person?.roleStartDate ?? ''),
    }))
    : []
  const normalizeCommodities = Array.isArray(payload.commodities) ? payload.commodities : []
  const normalizeSoftware = Array.isArray(payload.software_used) ? payload.software_used : []
  const normalizeProjects = Array.isArray(payload.key_projects) ? payload.key_projects : []
  const normalizedJobLinks = normalizeJobLinks(payload.job_links ?? payload.jobLinks ?? [])
  const normalizedRecentKeyHires = normalizeRecentKeyHires(payload.recent_key_hires)
  const normalizedSocialLinks = normalizeSocialLinks(payload.social_links)
  const normalizedRecentCapitalRaisings = normalizeRecentCapitalRaisings(payload.recent_capital_raisings)
  const normalizedBenefits = normalizeBenefits(payload.benefits)
  const manualJobsSource = Array.isArray(payload.manual_jobs) ? payload.manual_jobs : []
  const poolsRawSource = payload.talent_pools ?? payload.talentPools ?? []

  const poolsArray = (() => {
    if (Array.isArray(poolsRawSource)) return poolsRawSource
    if (poolsRawSource && typeof poolsRawSource === 'object') return Object.values(poolsRawSource)
    return []
  })()

  const talentPools: TalentPoolSummary[] = poolsArray.map((pool: any) => ({
    id: pool.id !== undefined ? Number(pool.id) : null,
    title: toSafeString(pool.title ?? ''),
    location: toSafeString(pool.location ?? ''),
    type: toSafeString(pool.type ?? ''),
    work_type: pool.work_type !== undefined && pool.work_type !== null ? Number(pool.work_type) : null,
    hiring_timeline: pool.hiring_timeline ?? null,
    timeline_date: formatDateLabel(pool.timeline_date ?? pool.timeline),
    timeline: pool.timeline ?? null,
  }))

  const rawMarketCap = payload.market_cap
  const rawMarketCapText = typeof rawMarketCap === 'string' ? rawMarketCap.trim() : ''
  const numericMarketCap = rawMarketCap !== null && rawMarketCap !== undefined && rawMarketCapText.toUpperCase() !== 'SUSPENDED'
    ? Number(rawMarketCap)
    : null
  const marketCap = rawMarketCapText.toUpperCase() === 'SUSPENDED'
    ? 'SUSPENDED'
    : (Number.isFinite(numericMarketCap) ? numericMarketCap : null)
  const cashInBank = parseMoney(payload.cash_in_bank ?? null)

  return {
    id: payload.id !== undefined ? Number(payload.id) : null,
    name: toSafeString(payload.name ?? ''),
    logo: toSafeString(payload.logo ?? ''),
    website: toSafeString(payload.website ?? ''),
    contact_email: toSafeString(payload.contact_email ?? payload.contactEmail ?? ''),
    summary: toSafeString(payload.summary ?? ''),
    industry: toSafeString(payload.industry ?? ''),
    industry_attributes: Array.isArray(payload.industry_attributes)
      ? payload.industry_attributes.filter(
          (group: any) => group && typeof group.label === 'string' && Array.isArray(group.values) && group.values.length
        )
      : [],
    employee_size: toSafeString(payload.employee_size ?? ''),
    headcount_growth_12_months: toSafeString(payload.headcount_growth_12_months ?? ''),
    average_employee_tenure: toSafeString(payload.average_employee_tenure ?? ''),
    head_office: toSafeString(payload.head_office ?? payload.office_address ?? ''),
    market_cap: marketCap,
    cash_in_bank: Number.isFinite(cashInBank ?? NaN) ? cashInBank : null,
    cash_in_bank_updated_at: toSafeString(payload.cash_in_bank_updated_at ?? '') || null,
    cash_runway: toSafeString(payload.cash_runway ?? ''),
    ticker: toSafeString(payload.ticker ?? ''),
    is_private_company: Boolean(payload.is_private_company),
    is_bookmarked: Boolean(payload.is_bookmarked),
    key_people: normalizePeople,
    key_projects: normalizeProjects.map((project: any) => ({
      name: toSafeString(project?.name ?? ''),
      stage: toSafeString(project?.stage ?? ''),
      description: toSafeString(project?.description ?? ''),
      location_label: toSafeString(project?.location_label ?? project?.locationLabel ?? project?.location ?? ''),
      location_lat: parseCoordinate(project?.location_lat ?? project?.locationLat ?? project?.lat),
      location_lng: parseCoordinate(project?.location_lng ?? project?.locationLng ?? project?.lng ?? project?.lon),
      commodities: Array.isArray(project?.commodities)
        ? project.commodities.map((item: string) => item.trim()).filter(Boolean)
        : [],
      activity_feed: Array.isArray(project?.activity_feed ?? project?.activityFeed)
        ? (project.activity_feed ?? project.activityFeed)
            .map((activity: any) => ({
              date: toSafeString(activity?.date ?? activity?.released_at ?? activity?.releasedAt ?? ''),
              text: toSafeString(activity?.text ?? activity?.update ?? activity?.description ?? ''),
            }))
            .filter((activity: { date: string; text: string }) => activity.date || activity.text)
            .sort((a: { date: string }, b: { date: string }) => b.date.localeCompare(a.date))
        : [],
    })),
    commodities: normalizeCommodities.map((item: string) => item.trim()).filter(Boolean),
    field_roster: toSafeString(payload.field_roster ?? ''),
    typical_hours: toSafeString(payload.typical_hours ?? ''),
    facilities: toSafeString(payload.facilities ?? ''),
    software_used: normalizeSoftware.map((item: string) => item.trim()).filter(Boolean),
    talentPools,
    job_links: normalizedJobLinks,
    recent_capital_raisings: normalizedRecentCapitalRaisings,
    recent_key_hires: normalizedRecentKeyHires,
    benefits: normalizedBenefits,
    social_links: normalizedSocialLinks,
    manual_jobs: manualJobsSource
      .map((job: any) => ({
        id: job.id !== undefined && job.id !== null ? Number(job.id) : null,
        title: toSafeString(job.title ?? ''),
        summary: toSafeString(job.summary ?? ''),
        location: toSafeString(job.location ?? ''),
        employment_type: toSafeString(job.employment_type ?? ''),
        roster: toSafeString(job.roster ?? ''),
        compensation_mode: toSafeString(job.compensation_mode ?? ''),
        compensation_amount: toSafeString(job.compensation_amount ?? ''),
        compensation_rate_type: toSafeString(job.compensation_rate_type ?? ''),
        date_posted: toSafeString(job.date_posted ?? ''),
        closing_date: toSafeString(job.closing_date ?? ''),
        job_url: toSafeString(job.job_url ?? ''),
        job_slug: toSafeString(job.job_slug ?? ''),
        is_imported: Boolean(job.is_imported),
        source_origin: toSafeString(job.source_origin ?? ''),
        source_label: toSafeString(job.source_label ?? ''),
      }))
      .filter((job: CompanyManualJob) => Boolean(job.title)),
  }
}


const fetchCompany = async () => {
  const slug = route.params.slug as string | undefined
  if (!slug) {
    error.value = 'Missing company identifier.'
    isLoading.value = false
    return
  }

  isLoading.value = true
  error.value = null

  try {
    const { data } = await getContractorCompanyProfile(slug)
    company.value = normalizeCompanyPayload(data)
  } catch (err: any) {
    console.error('Failed to load company', err)
    error.value = err?.response?.data?.error ?? 'Failed to load company information.'
    company.value = null
  } finally {
    isLoading.value = false
  }
}

const toggleCompanyBookmark = async () => {
  if (!company.value?.id || isBookmarkSaving.value) return

  const companyId = company.value.id
  const previousBookmarkedState = company.value.is_bookmarked
  const nextBookmarkedState = !company.value.is_bookmarked
  isBookmarkSaving.value = true
  company.value = {
    ...company.value,
    is_bookmarked: nextBookmarkedState,
  }

  try {
    if (nextBookmarkedState) {
      await bookmarkContractorCompany(companyId)
    } else {
      await removeContractorCompanyBookmark(companyId)
    }
  } catch (error) {
    console.error('Failed to update company bookmark', error)
    if (company.value?.id === companyId) {
      company.value = {
        ...company.value,
        is_bookmarked: previousBookmarkedState,
      }
    }
  } finally {
    isBookmarkSaving.value = false
  }
}

watch(
  () => route.params.slug,
  () => {
    fetchCompany()
  },
  { immediate: true }
)

const formattedMarketCap = computed<number | string | null>(() => {
  if (!company.value?.market_cap) return null
  if (company.value.market_cap === 'SUSPENDED') return company.value.market_cap
  return Number.isFinite(company.value.market_cap) ? company.value.market_cap : null
})

const formatAudCurrency = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return ''
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value)
}

const formatCapitalRaisingAmount = (value: unknown): string => {
  const amount = toSafeString(value)
  if (!amount || !/\d/.test(amount) || /^(?:aud|a\$)/i.test(amount) || amount.includes('$')) {
    return amount
  }

  return `$${amount}`
}

const formatCashRunway = (value: unknown): string => {
  const amount = toSafeString(value)
  if (!amount) return ''
  if (/[a-z]/i.test(amount)) return amount

  const numericAmount = Number(amount)
  if (!Number.isFinite(numericAmount)) return amount

  return `${amount} ${numericAmount === 1 ? 'quarter' : 'quarters'}`
}

const formatPercentValue = (value: unknown): string => {
  const text = toSafeString(value)
  if (!text) return ''
  return text.includes('%') ? text : `${text}%`
}

const formatYearsValue = (value: unknown): string => {
  const text = toSafeString(value)
  if (!text) return ''
  return /\b(year|years|yr|yrs|month|months|mo|mos)\b/i.test(text) ? text : `${text} years`
}

const formattedMarketCapLabel = computed(() => {
  if (formattedMarketCap.value === null) return ''
  return formatMarketCap(formattedMarketCap.value)
})

const cashInBankLabel = computed(() => formatAudCurrency(company.value?.cash_in_bank ?? null))

const cashRunwayLabel = computed(() => formatCashRunway(company.value?.cash_runway ?? ''))

const cashInBankDateLabel = computed(() => {
  const raw = company.value?.cash_in_bank_updated_at
  if (!raw) return ''
  return formatDateAsDayMonthYear(raw)
})

const showMarketCap = computed(
  () =>
    formattedMarketCap.value !== null &&
    !!company.value?.ticker &&
    !company.value?.is_private_company
)

const displayWebsite = computed(() => {
  const raw = company.value?.website
  if (!raw) return ''
  return raw.replace(/^https?:\/\//i, '')
})

const websiteHref = computed(() => {
  const raw = company.value?.website
  if (!raw) return ''
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
})

const contactEmailHref = computed(() => {
  const raw = company.value?.contact_email
  if (!raw) return ''
  return `mailto:${raw}`
})

const socialLinkDisplayItems = computed(() => {
  const links = Array.isArray(company.value?.social_links) ? company.value.social_links : []

  return links.map((socialLink) => {
    const labelMap: Record<SocialLinkType, string> = {
      linkedin: 'LinkedIn',
      facebook: 'Facebook',
      instagram: 'Instagram',
      x: 'X',
      youtube: 'YouTube',
    }

    return {
      ...socialLink,
      label: labelMap[socialLink.type],
      displayUrl: normalizeExternalUrl(socialLink.url).replace(/^https?:\/\//i, ''),
    }
  })
})

const companyJobLinks = computed<JobLinkDisplayItem[]>(() => {
  const links = Array.isArray(company.value?.job_links) ? company.value.job_links : []

  return links.map((jobLink) => {
    if (jobLink.type === 'linkedin') {
      return {
        ...jobLink,
        typeLabel: 'LinkedIn',
        ctaLabel: 'View on LinkedIn',
        cardClass: 'from-sky-50 via-white to-blue-50',
        icon: Briefcase,
      }
    }

    if (jobLink.type === 'seek') {
      return {
        ...jobLink,
        typeLabel: 'Seek',
        ctaLabel: 'View on SEEK',
        cardClass: 'from-rose-50 via-white to-orange-50',
        icon: Globe,
      }
    }

    return {
      ...jobLink,
      typeLabel: 'Other',
      ctaLabel: 'Open careers page',
      cardClass: 'from-emerald-50 via-white to-teal-50',
      icon: Building2,
    }
  })
})

const operationsVisible = computed(() => {
  if (!company.value) return false
  return Boolean(
    company.value.field_roster ||
      company.value.typical_hours ||
      company.value.facilities ||
      company.value.software_used.length
  )
})

const personName = (person: KeyPerson = {}): string => {
  const first = toSafeString(person.first_name ?? person.name?.split(' ')[0] ?? '')
  const last = toSafeString(person.last_name ?? person.name?.split(' ').slice(1).join(' ') ?? '')
  const legacy = toSafeString(person.name ?? '')
  const combined = `${first} ${last}`.trim()
  return combined || legacy || '—'
}

const personRole = (person: KeyPerson = {}): string =>
  toSafeString(person.role ?? person.position ?? '')

const personRoleTenure = (person: KeyPerson = {}): string => {
  const raw = toSafeString(person.role_start_date ?? person.roleStartDate ?? '')
  if (!raw) return ''

  const start = new Date(`${raw.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(start.getTime())) return ''

  const now = new Date()
  if (start > now) return ''

  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
  if (now.getDate() < start.getDate()) months -= 1
  months = Math.max(months, 0)

  const years = Math.floor(months / 12)
  const remainingMonths = months % 12
  const parts = []

  if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`)
  if (remainingMonths > 0) parts.push(`${remainingMonths} ${remainingMonths === 1 ? 'month' : 'months'}`)

  return parts.length ? parts.join(' ') : 'Less than 1 month'
}

const personInitial = (person: KeyPerson = {}): string => {
  const name = personName(person)
  return name ? name.charAt(0).toUpperCase() : '?'
}

const formatCompensation = (job: CompanyManualJob): string => {
  const amount = Number(job.compensation_amount)
  if (Number.isFinite(amount) && amount > 0) {
    const formatted = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0,
    }).format(amount)

    if (job.compensation_mode === 'salary') {
      return formatted
    }

    if (job.compensation_mode === 'rate' && job.compensation_rate_type) {
      const labelMap: Record<string, string> = {
        hour: 'hour',
        day: 'day',
        month: 'month',
      }

      return `${formatted} / ${labelMap[job.compensation_rate_type] ?? job.compensation_rate_type}`
    }
  }

  return ''
}
</script>
